import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Spin } from 'antd'
import { useAuth } from './stores/auth'
import { hasRouteAccess } from './utils/permissions'
import MainLayout from './layouts/MainLayout'
import Login from './pages/Login'
import SetPassword from './pages/SetPassword'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'

const Dashboard = lazy(() => import('./pages/Dashboard'))
const Customers = lazy(() => import('./pages/Customers'))
const Payments = lazy(() => import('./pages/Payments'))
const AdAccounts = lazy(() => import('./pages/AdAccounts'))
const OpeningOrders = lazy(() => import('./pages/OpeningOrders'))
const MediaTodos = lazy(() => import('./pages/MediaTodos'))
const AccountAnalysis = lazy(() => import('./pages/AccountAnalysis'))
const PortAccountDashboard = lazy(() => import('./pages/PortAccountDashboard'))
const Consumptions = lazy(() => import('./pages/Consumptions'))
const Refunds = lazy(() => import('./pages/Refunds'))
const BmManagement = lazy(() => import('./pages/BmManagement'))
const Policies = lazy(() => import('./pages/Policies'))
const BatchImport = lazy(() => import('./pages/BatchImport'))
const UserManagement = lazy(() => import('./pages/UserManagement'))
const Approvals = lazy(() => import('./pages/Approvals'))
const ReceivingAccounts = lazy(() => import('./pages/ReceivingAccounts'))
const FundMonitor = lazy(() => import('./pages/FundMonitor'))
const AuditLogs = lazy(() => import('./pages/AuditLogs'))
const Staging = lazy(() => import('./pages/Staging'))
const ExtensionHub = lazy(() => import('./pages/extension/ExtensionHub'))
const InviteAssign = lazy(() => import('./pages/extension/InviteAssign'))
const PixelShare = lazy(() => import('./pages/extension/PixelShare'))
const BatchRename = lazy(() => import('./pages/extension/BatchRename'))
const AdminKillSwitch = lazy(() => import('./pages/AdminKillSwitch'))
const Reconcile = lazy(() => import('./pages/Reconcile'))
const CardSpendReconcile = lazy(() => import('./pages/CardSpendReconcile'))
const RevenueAnalytics = lazy(() => import('./pages/RevenueAnalytics'))
const SlashCardGroupDetail = lazy(() => import('./pages/SlashCardGroupDetail'))
const SlashCardDetailPage = lazy(() => import('./pages/SlashCardDetailPage'))
const SlashCardsHub = lazy(() => import('./pages/SlashCardsHub'))
const CashbackRates = lazy(() => import('./pages/CashbackRates'))
const PhotonPayCredentials = lazy(() => import('./pages/PhotonPayCredentials'))
const WasabiCredentials = lazy(() => import('./pages/WasabiCredentials'))
const WasabiHub = lazy(() => import('./pages/WasabiHub'))
const WasabiCardDetail = lazy(() => import('./pages/WasabiCardDetail'))
const WasabiAccountTransactions = lazy(() => import('./pages/WasabiAccountTransactions'))
const WasabiWalletTransactions = lazy(() => import('./pages/WasabiWalletTransactions'))
const PhotonPayHub = lazy(() => import('./pages/PhotonPayHub'))
const PhotonPayCardholderDetail = lazy(() => import('./pages/PhotonPayCardholderDetail'))
const PhotonPayCardholderGroupDetail = lazy(() => import('./pages/PhotonPayCardholderGroupDetail'))
const PhotonPayCardDetail = lazy(() => import('./pages/PhotonPayCardDetail'))
const CardPlatforms = lazy(() => import('./pages/CardPlatforms'))

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />
}

function RoleRoute({ children, path }: { children: React.ReactNode; path: string }) {
  const { isAuthenticated, role } = useAuth()
  if (!isAuthenticated()) return <Navigate to="/login" replace />
  if (!hasRouteAccess(role, path)) return <Navigate to="/" replace />
  return <>{children}</>
}

function PageFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
      <Spin size="large" />
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <MainLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="customers" element={<RoleRoute path="/customers"><Customers /></RoleRoute>} />
            <Route path="payments" element={<RoleRoute path="/payments"><Payments /></RoleRoute>} />
            <Route path="ad-accounts" element={<RoleRoute path="/ad-accounts"><AdAccounts /></RoleRoute>} />
            <Route path="opening-orders" element={<RoleRoute path="/opening-orders"><OpeningOrders /></RoleRoute>} />
            <Route path="media-todos" element={<RoleRoute path="/media-todos"><MediaTodos /></RoleRoute>} />
            <Route path="account-analysis" element={<RoleRoute path="/account-analysis"><AccountAnalysis /></RoleRoute>} />
            <Route path="port-account-dashboard" element={<RoleRoute path="/port-account-dashboard"><PortAccountDashboard /></RoleRoute>} />
            <Route path="consumptions" element={<RoleRoute path="/consumptions"><Consumptions /></RoleRoute>} />
            <Route path="refunds" element={<RoleRoute path="/refunds"><Refunds /></RoleRoute>} />
            <Route path="bm" element={<RoleRoute path="/bm"><BmManagement /></RoleRoute>} />
            <Route path="policies" element={<RoleRoute path="/policies"><Policies /></RoleRoute>} />
            {/* /policy-overview 已合并进 /policies?tab=history (2026-05-01) */}
            <Route path="policy-overview" element={<Navigate to="/policies?tab=history" replace />} />
            <Route path="batch-import" element={<RoleRoute path="/batch-import"><BatchImport /></RoleRoute>} />
            <Route path="users" element={<RoleRoute path="/users"><UserManagement /></RoleRoute>} />
            <Route path="approvals" element={<RoleRoute path="/approvals"><Approvals /></RoleRoute>} />
            <Route path="receiving-accounts" element={<RoleRoute path="/receiving-accounts"><ReceivingAccounts /></RoleRoute>} />
            <Route path="fund-monitor" element={<RoleRoute path="/fund-monitor"><FundMonitor /></RoleRoute>} />
            <Route path="audit-logs" element={<RoleRoute path="/audit-logs"><AuditLogs /></RoleRoute>} />
            <Route path="staging" element={<RoleRoute path="/staging"><Staging /></RoleRoute>} />
            {/* /customer-summary-entry 已下线 (2026-05-01) → 重定向到批量导入并触发引导 Alert */}
            <Route path="customer-summary-entry" element={<Navigate to="/batch-import?guide=manual-daily" replace />} />
            <Route path="extension" element={<RoleRoute path="/extension"><ExtensionHub /></RoleRoute>} />
            <Route path="extension/invite-assign" element={<RoleRoute path="/extension/invite-assign"><InviteAssign /></RoleRoute>} />
            <Route path="extension/pixel-share" element={<RoleRoute path="/extension/pixel-share"><PixelShare /></RoleRoute>} />
            <Route path="extension/batch-rename" element={<RoleRoute path="/extension/batch-rename"><BatchRename /></RoleRoute>} />
            <Route path="admin/kill-switch" element={<RoleRoute path="/admin/kill-switch"><AdminKillSwitch /></RoleRoute>} />
            <Route path="reconcile" element={<RoleRoute path="/reconcile"><Reconcile /></RoleRoute>} />
            <Route path="card-spend-reconcile" element={<RoleRoute path="/card-spend-reconcile"><CardSpendReconcile /></RoleRoute>} />
            <Route path="analytics" element={<RoleRoute path="/analytics"><RevenueAnalytics /></RoleRoute>} />
            <Route path="cashback-rates" element={<RoleRoute path="/cashback-rates"><CashbackRates /></RoleRoute>} />
            <Route path="card-platforms" element={<RoleRoute path="/card-platforms"><CardPlatforms /></RoleRoute>} />
            {/* /slash/cards = Slash 卡片父页 (Tab: 预算组 / 全部卡片), 2026-05-09 Wave 14 合并 */}
            <Route path="slash/cards" element={<RoleRoute path="/slash/cards"><SlashCardsHub /></RoleRoute>} />
            <Route path="slash/cards/:id" element={<RoleRoute path="/slash/cards"><SlashCardDetailPage /></RoleRoute>} />
            {/* 兼容老书签: /slash/card-groups → /slash/cards?tab=groups */}
            <Route path="slash/card-groups" element={<Navigate to="/slash/cards?tab=groups" replace />} />
            <Route path="slash/card-groups/:id" element={<RoleRoute path="/slash/card-groups"><SlashCardGroupDetail /></RoleRoute>} />
            {/* PhotonPay 第二卡台 Wave 1.1 (2026-05-14) — 凭证管理 founder-only */}
            <Route path="photonpay/credentials" element={<RoleRoute path="/photonpay/credentials"><PhotonPayCredentials /></RoleRoute>} />
            {/* WasabiCard 第三卡台 阶段1 (2026-09-02) — 凭证管理 founder-only。
                阶段3 起凭证已收成 Hub 的 founder-only tab, 但这条路由保留:
                老书签与既有跳转还指着它, 删掉会 404。*/}
            <Route path="wasabi/credentials" element={<RoleRoute path="/wasabi/credentials"><WasabiCredentials /></RoleRoute>} />
            {/* WasabiCard 阶段3 批次2 (2026-09-02) — 只读镜像 Hub。
                所有子 tab 共用 /wasabi 这一个权限 key (照光子易的做法, 不为每个
                tab 单开 key); tab 级的 founder-only 闸门在 WasabiHub 内部。*/}
            <Route path="wasabi" element={<RoleRoute path="/wasabi"><WasabiHub /></RoleRoute>} />
            <Route path="wasabi/cards/:id" element={<RoleRoute path="/wasabi"><WasabiCardDetail /></RoleRoute>} />
            <Route path="wasabi/accounts/:id/transactions" element={<RoleRoute path="/wasabi"><WasabiAccountTransactions /></RoleRoute>} />
            <Route path="wasabi/wallet-transactions" element={<RoleRoute path="/wasabi"><WasabiWalletTransactions /></RoleRoute>} />
            {/* PhotonPay Wave 1.2 (2026-05-15) — 持卡人 Hub (镜像 Slash) */}
            <Route path="photonpay" element={<RoleRoute path="/photonpay"><PhotonPayHub /></RoleRoute>} />
            <Route path="photonpay/cardholders/:id" element={<RoleRoute path="/photonpay"><PhotonPayCardholderDetail /></RoleRoute>} />
            {/* PhotonPay Wave 1.2.6 (2026-05-15) — 预算组 (Wave 2.1 前端文案与 Slash 对齐, 后端字段仍叫 cardholder_group_id) */}
            <Route path="photonpay/cardholder-groups/:id" element={<RoleRoute path="/photonpay"><PhotonPayCardholderGroupDetail /></RoleRoute>} />
            {/* PhotonPay Wave 1.3 (2026-05-16) — 卡片详情 */}
            <Route path="photonpay/cards/:id" element={<RoleRoute path="/photonpay"><PhotonPayCardDetail /></RoleRoute>} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
